<template>
  <span class="icon-component" :class="iconClasses" :style="iconStyles">
    <span
      v-if="iconExists && svgContent"
      v-html="svgContent"
      class="icon-svg"/>

    <span v-else class="icon-fallback">{{ name }}</span>
  </span>
</template>

<script>
import { resolveIconColor } from '@/constants/iconSemanticColors';

// Available icons list for documentation
export const availableIcons = [
  'expand-right', 'expand-up', 'expand-down', 'expand-left',
  'arrow-up', 'arrow-right', 'arrow-down', 'arrow-left',
  'settings', 'check', 'eye', 'edit',
  'dots-vertical', 'dots-horizontal', 'star', 'star-full',
  'minus', 'plus', 'cross',
  'check-circle', 'question-circle', 'warning-circle', 'info-circle',
  'trash', 'email', 'eye-closed', 'password',
  'search', 'filter', 'copy', 'upload', 'microphone',
  'play', 'pause', 'menu', 'clock', 'calendar',
  'location', 'notification', 'ai', 'homepage',
  'profile-circle', 'profile', 'folder', 'logout',
  'volume-up', 'start-call', 'agent', 'tools', 'rag', 'text-box',
  'timer', 'reset',
];

export const iconVariants = ['stroke', 'solid'];
export const iconSizes = ['xs', 'sm', 'md', 'lg', 'xl'];

// SVG cache to avoid repeated requests
const svgCache = new Map();

export default {
  name: 'Icon',
  data() {
    return {
      svgContent: null,
      isLoading: false,
    };
  },
  props: {
    name: {
      type: String,
      required: true,
      default: 'placeholder',
    },
    variant: {
      type: String,
      default: 'stroke',
      validator: (value) => ['stroke', 'solid'].includes(value),
    },
    size: {
      type: [String, Number],
      default: '24',
    },
    /** Ham hex (#...) veya semantik: default, muted, tertiary, onPrimary, primary, info, warning, success, error */
    color: {
      type: String,
      default: null,
    },
    clickable: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    iconName() {
      // Construct the icon filename
      return `${this.name}-${this.variant}.svg`;
    },
    iconUrl() {
      // Return the public path to the icon
      return `/icons/${this.iconName}`;
    },
    iconExists() {
      // Check if icon exists in our available list
      return this.name && this.variant && availableIcons.includes(this.name);
    },
    iconClasses() {
      return {
        'icon-clickable': this.clickable,
        [`icon-size-${this.size}`]: typeof this.size === 'string' && ['xs', 'sm', 'md', 'lg', 'xl'].includes(this.size),
      };
    },
    resolvedColor() {
      return resolveIconColor(this.color);
    },
    iconStyles() {
      const styles = {};

      // Handle size
      if (typeof this.size === 'number' || !Number.isNaN(Number(this.size))) {
        styles.width = `${this.size}px`;
        styles.height = `${this.size}px`;
      }

      if (this.resolvedColor) {
        styles.color = this.resolvedColor;
      }

      return styles;
    },
  },
  watch: {
    iconUrl: {
      immediate: true,
      handler(newUrl) {
        if (this.iconExists) {
          this.loadSvg(newUrl);
        }
      },
    },
  },
  methods: {
    async loadSvg(url) {
      if (!url) return;

      // Check cache first
      if (svgCache.has(url)) {
        this.svgContent = svgCache.get(url);
        return;
      }

      this.isLoading = true;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to load SVG: ${response.status}`);
        }

        const svgText = await response.text();

        // Process SVG to make it styleable
        const processedSvg = this.processSvg(svgText);

        // Cache the result
        svgCache.set(url, processedSvg);

        this.svgContent = processedSvg;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(`Failed to load icon: ${this.name}`, error);
        this.svgContent = null;
      } finally {
        this.isLoading = false;
      }
    },

    processSvg(svgText) {
      // Remove width and height attributes to make SVG responsive
      let processed = svgText.replace(/width="[^"]*"/gi, '');
      processed = processed.replace(/height="[^"]*"/gi, '');

      // Add currentColor to fill and stroke attributes for styling
      processed = processed.replace(/fill="(?!none)[^"]*"/gi, 'fill="currentColor"');
      processed = processed.replace(/stroke="(?!none)[^"]*"/gi, 'stroke="currentColor"');

      // Add viewBox if missing
      if (!processed.includes('viewBox')) {
        processed = processed.replace('<svg', '<svg viewBox="0 0 24 24"');
      }

      return processed;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';
@import '@/styles/variables.scss';

.icon-component {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: $light-foreground-1;
  transition: all 0.2s ease;

  &.icon-clickable {
    cursor: pointer;

    &:hover {
      color: $light-primary;
      transform: scale(1.1);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  // Size presets
  &.icon-size-xs {
    width: 12px;
    height: 12px;
  }

  &.icon-size-sm {
    width: 16px;
    height: 16px;
  }

  &.icon-size-md {
    width: 24px;
    height: 24px;
  }

  &.icon-size-lg {
    width: 32px;
    height: 32px;
  }

  &.icon-size-xl {
    width: 48px;
    height: 48px;
  }
}

.icon-svg {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  :deep(svg) {
    width: 100%;
    height: 100%;
    display: block;

    // Preserve 'none' values
    [fill="none"] {
      fill: none !important;
    }

    [stroke="none"] {
      stroke: none !important;
    }
  }
}

.icon-fallback {
  font-size: $caption-2-size;
  font-weight: 500;
  color: $light-foreground-3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style>
